import { type Database } from "@fitness-app/supabase";

// TRE-1655 — Challenge feed: trainer announcements, participant posts, comments and reactions.

export type ChallengePostEntity = Database["public"]["Tables"]["challenge_post"]["Row"];
export type ChallengePostCommentEntity = Database["public"]["Tables"]["challenge_post_comment"]["Row"];
export type ChallengePostReactionEntity = Database["public"]["Tables"]["challenge_post_reaction"]["Row"];

/**
 * How the feed behaves for participants — stored on `challenge.feedMode` and enforced
 * server-side by the RLS policies on challenge_post / challenge_post_comment.
 */
export enum ChallengeFeedMode {
  /** No feed at all (default — every challenge created before TRE-1655). */
  None = "none",
  /** Only trainers post; participants read (and may react). */
  Readonly = "readonly",
  /** Only trainers post; participants may comment. */
  Comments = "comments",
  /** Participants may post as well. */
  Full = "full",
}

export const challengeFeedEnabled = (mode: ChallengeFeedMode | string | null | undefined): boolean =>
  mode != null && mode !== ChallengeFeedMode.None;

export const canCommentInFeed = (mode: ChallengeFeedMode | string | null | undefined): boolean =>
  mode === ChallengeFeedMode.Comments || mode === ChallengeFeedMode.Full;

export const canPostInFeed = (mode: ChallengeFeedMode | string | null | undefined): boolean =>
  mode === ChallengeFeedMode.Full;

export const asChallengeFeedMode = (value: string | null | undefined): ChallengeFeedMode =>
  Object.values(ChallengeFeedMode).includes(value as ChallengeFeedMode)
    ? (value as ChallengeFeedMode)
    : ChallengeFeedMode.None;

export enum ChallengePostMediaType {
  Image = "image",
  Video = "video",
}

/** One entry of the `challenge_post.media` jsonb array. */
export type ChallengePostMedia = {
  url: string;
  type: ChallengePostMediaType;
  thumbnailUrl?: string | null;
  width?: number | null;
  height?: number | null;
};

/**
 * TRE-1730 — attachments render as ONE horizontally scrollable row of thumbnails.
 *
 * A post is a message, not a gallery: the attachments announce themselves and get out of the way,
 * and the full frame is one click away in the lightbox. Fixing the row's HEIGHT (rather than the
 * width, as the old `w-full max-h-[420px]` did) is what keeps a portrait upload from taking over
 * the card — each thumbnail is this tall and only as wide as its own shape needs.
 */
export const CHALLENGE_MEDIA_THUMB_HEIGHT = 132;
/** A reply's attachments are an aside, not a second post. */
export const CHALLENGE_COMMENT_MEDIA_THUMB_HEIGHT = 88;

/**
 * Aspect-ratio clamp for a thumbnail.
 *
 * Uploads are whatever the phone produced — a 9:19 screen recording, a panorama, a square photo.
 * At their true ratio the row turns into slivers and banners; clamping keeps every thumbnail in
 * the same shape family, so the row reads as a row.
 */
export const MIN_CHALLENGE_MEDIA_ASPECT_RATIO = 3 / 4;
export const MAX_CHALLENGE_MEDIA_ASPECT_RATIO = 16 / 9;
/** Used when the upload carried no dimensions — every post written before TRE-1655 stored none. */
export const DEFAULT_CHALLENGE_MEDIA_ASPECT_RATIO = 4 / 3;

/** Width ÷ height for an attachment's thumbnail, clamped to the range above. */
export const challengeMediaAspectRatio = (item: Pick<ChallengePostMedia, "width" | "height">): number => {
  const { width, height } = item;
  if (!width || !height || width <= 0 || height <= 0) {
    return DEFAULT_CHALLENGE_MEDIA_ASPECT_RATIO;
  }
  return Math.min(MAX_CHALLENGE_MEDIA_ASPECT_RATIO, Math.max(MIN_CHALLENGE_MEDIA_ASPECT_RATIO, width / height));
};

/** The emoji palette offered by the clients; any emoji is accepted by the DB. */
export const CHALLENGE_POST_REACTIONS = ["❤️", "🔥", "💪", "👏", "😍", "🎉"] as const;

export const MAX_CHALLENGE_POST_TITLE_LENGTH = 200;
export const MAX_CHALLENGE_POST_LENGTH = 5000;
export const MAX_CHALLENGE_COMMENT_LENGTH = 2000;
export const MAX_CHALLENGE_POST_MEDIA = 10;
/** A comment is a reply, not a gallery — fewer slots than a post, same storage shape. */
export const MAX_CHALLENGE_COMMENT_MEDIA = 4;

export interface ChallengePost extends Omit<ChallengePostEntity, "media"> {
  media: ChallengePostMedia[];
}

export type ChallengePostComment = ChallengePostCommentEntity;
export type ChallengePostReaction = ChallengePostReactionEntity;

/** Reaction summary attached to a feed row: emoji → how many people used it. */
export type ChallengePostReactionSummary = Record<string, number>;

/** A row of the `challenge_feed` RPC, already narrowed for the clients. */
export type ChallengeFeedPost = {
  id: string;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  authorIsTrainer: boolean;
  /** Optional headline — an announcement feed needs something to scan. */
  title: string | null;
  content: string;
  media: ChallengePostMedia[];
  pinned: boolean;
  commentCount: number;
  reactions: ChallengePostReactionSummary;
  myReactions: string[];
  isMine: boolean;
};

/** A row of the `challenge_post_comments` RPC. */
export type ChallengeFeedComment = {
  id: string;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  authorIsTrainer: boolean;
  content: string;
  media: ChallengePostMedia[];
  isMine: boolean;
};

export type ChallengePostReactor = {
  userId: string;
  emoji: string;
  displayName: string;
  avatarUrl: string | null;
};

const FALLBACK_AUTHOR_NAME = "Uczestniczka";

/** Parses the jsonb `media` column into typed entries, dropping anything malformed. */
export const parseChallengePostMedia = (raw: unknown): ChallengePostMedia[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object") {
      return [];
    }
    const item = entry as Partial<ChallengePostMedia>;
    if (typeof item.url !== "string" || !item.url) {
      return [];
    }
    return [
      {
        url: item.url,
        type: item.type === ChallengePostMediaType.Video ? ChallengePostMediaType.Video : ChallengePostMediaType.Image,
        thumbnailUrl: typeof item.thumbnailUrl === "string" ? item.thumbnailUrl : null,
        width: typeof item.width === "number" ? item.width : null,
        height: typeof item.height === "number" ? item.height : null,
      },
    ];
  });
};

export const parseChallengeReactionSummary = (raw: unknown): ChallengePostReactionSummary => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).flatMap(([emoji, count]) =>
      typeof count === "number" && count > 0 ? [[emoji, count] as const] : [],
    ),
  );
};

export const parseMyReactions = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((emoji): emoji is string => typeof emoji === "string") : [];

export const authorNameOrFallback = (name: string | null | undefined): string =>
  name?.trim() ? name.trim() : FALLBACK_AUTHOR_NAME;

/**
 * Normalizes post/comment markdown before it is rendered or stored.
 *
 * The WYSIWYG editor (Plate) writes two things the renderers double up on:
 *
 * - an EMPTY paragraph is serialized as a lone zero-width space (U+200B), which survives as a
 *   full-height blank paragraph — the "why is there so much air between the lines" gap;
 * - a soft line break is serialized as a trailing backslash, which markdown turns into `<br>` —
 *   and the surrounding `white-space: pre-wrap` (needed for plain `\n` coming from the mobile
 *   composer) then breaks the same line a SECOND time.
 *
 * Dropping the zero-width spaces and the backslashes leaves one plain `\n` per line break, which
 * every surface renders identically. Applied on read too, so posts written before this fix render
 * correctly without a data migration.
 */
export const normalizeChallengeMarkdown = (content: string): string =>
  content
    .replace(/\u200b/g, "")
    // A backslash immediately before a newline is markdown's hard break — pre-wrap already breaks.
    .replace(/\\\n/g, "\n")
    // Collapse the blank paragraphs those substitutions leave behind.
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * TRE-1655 — who manages a challenge.
 *
 * The organisation owner is implicit (they manage everything); this table names the team members
 * the owner delegates a given challenge to. An EMPTY list means owner-only — `challenge_can_manage`
 * checks exactly that, and it gates every challenge policy, not just the board.
 */
export type ChallengeManagerEntity = Database["public"]["Tables"]["challenge_manager"]["Row"];

export type ChallengeManager = ChallengeManagerEntity & {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

/** Notification switches a manager can flip per challenge. */
export const CHALLENGE_MANAGER_NOTIFICATIONS = [
  { key: "notifyOnParticipantPost", label: "Nowy post uczestniczki" },
  { key: "notifyOnTrainerPostComment", label: "Komentarz pod postem trenera" },
  { key: "notifyOnParticipantPostComment", label: "Komentarz pod postem uczestniczki" },
] as const;

export type ChallengeManagerNotificationKey = (typeof CHALLENGE_MANAGER_NOTIFICATIONS)[number]["key"];
